"use client";

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useFormik } from "formik";
import type { FormikErrors } from "formik";
import { OTPInput } from "@/design-elements/otp-input";
import { validateOtp } from "@/lib/utils";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
import {
	getOtp,
	verifyOtpForMobile,
} from "@/app/product/_actions/user.actions";
import { clientLogger } from "@/lib/logger";
import { useAppData } from "@/store/appStore";
import { useRouter } from "next/navigation";
import { Logo } from "@/design-elements/logo";
import { trackEvent, GTMEvent } from "@/lib/utils/gtm-events";
import EditIcon from "@/components/icons/EditIcon";

interface StepMobileOtpProps {
	onSubmit: (otp: string) => void;
	onSkip?: () => void;
	onBack?: () => void;
	initialOtp?: string;
	mobileForOtp?: string;
	shouldRefreshAfterLogin?: boolean;
}

interface FormValues {
	otp: string;
	captcha: string | null;
}

const OTP_COPY = {
	heroTitle: "Verify your mobile",
	label: "Enter OTP",
	verify: "Verify",
	verifying: "Verifying...",
	resendPrompt: "Didn't receive the OTP?",
	resendCta: "Resend",
	resendCountdown: (seconds: number) => `Resend in ${seconds}s`,
	skip: "Skip",
	errorInvalidOtp: "Please enter a valid 5-digit OTP.",
	errorVerificationFailed: "OTP verification failed. Please try again.",
	errorUnexpected: "An unexpected error occurred.",
	errorResendFailed: "Failed to resend OTP. Please try again.",
	errorResendUnexpected: "An unexpected error occurred while resending OTP.",
	errorMissingMobile: "Add a mobile number to request a new OTP.",
	logSkip: "Onboarding: Mobile OTP step skipped",
	logSuccess: "Onboarding: Mobile OTP verified successfully",
};

const StepMobileOtp: React.FC<StepMobileOtpProps> = ({
	onSubmit,
	onSkip,
	onBack,
	initialOtp = "",
	mobileForOtp,
	shouldRefreshAfterLogin = false,
}) => {
	const router = useRouter();
	const [appData, , setCookielessData] = useAppData();
	const [resendTimerActive, setResendTimerActive] = useState(true);
	const turnstileRef = useRef<TurnstileInstance>(null);
	const [resendTimerCount, setResendTimerCount] = useState(60);
	const [lastFailedOtp, setLastFailedOtp] = useState<string | null>(null);

	const targetMobile = useMemo(() => {
		if (mobileForOtp && mobileForOtp.trim().length > 0) {
			return mobileForOtp.trim();
		}
		if (appData?.user?.mobileNo) {
			return String(appData.user.mobileNo);
		}
		if (appData?.phoneNumber) {
			return String(appData.phoneNumber);
		}
		return undefined;
	}, [appData?.phoneNumber, appData?.user?.mobileNo, mobileForOtp]);

	const formik = useFormik<FormValues>({
		initialValues: { otp: initialOtp, captcha: null },
		validate: (values) => {
			const errors: FormikErrors<FormValues> = {};
			if (!values.otp || !validateOtp(values.otp)) {
				errors.otp = OTP_COPY.errorInvalidOtp;
			}
			return errors;
		},
		onSubmit: async (values, { setSubmitting, setFieldError }) => {
			try {
				const result = await verifyOtpForMobile(values.otp, values.captcha);
				if (
					result &&
					typeof result === "object" &&
					"success" in result &&
					result.success
				) {
					trackEvent(GTMEvent.MOBILE_VERIFIED, {
						page_path: "Onboarding page",
					});

					if (appData?.user && targetMobile) {
						setCookielessData({
							user: {
								...appData.user,
								mobileNo: parseInt(targetMobile, 10),
							},
						});
					}

					setLastFailedOtp(values.otp);
					onSubmit(values.otp);
					clientLogger.info(OTP_COPY.logSuccess, {
						mobileForOtp: targetMobile,
					});
					setFieldError("otp", undefined);

					if (shouldRefreshAfterLogin) {
						setTimeout(() => {
							router.refresh();
						}, 0);
					}
				} else {
					setLastFailedOtp(values.otp);
					turnstileRef.current?.reset();
					formik.setFieldValue("captcha", null, false);
					setFieldError("otp", OTP_COPY.errorVerificationFailed);
					setSubmitting(false);
				}
			} catch (err) {
				setLastFailedOtp(values.otp);
				turnstileRef.current?.reset();
				formik.setFieldValue("captcha", null, false);
				setFieldError(
					"otp",
					err instanceof Error ? err.message : OTP_COPY.errorUnexpected,
				);
				setSubmitting(false);
			}
		},
	});

	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (resendTimerActive && resendTimerCount > 0) {
			interval = setInterval(() => {
				setResendTimerCount((prev) => prev - 1);
			}, 1000);
		} else if (resendTimerCount === 0) {
			setResendTimerActive(false);
		}
		return () => clearInterval(interval);
	}, [resendTimerActive, resendTimerCount]);

	useEffect(() => {
		if (
			formik.values.captcha &&
			validateOtp(formik.values.otp) &&
			!formik.isSubmitting &&
			!formik.errors.otp &&
			formik.values.otp !== lastFailedOtp
		) {
			formik.setFieldTouched("otp", true, false);
			queueMicrotask(() => {
				formik.submitForm();
			});
		}
	}, [
		formik.values.captcha,
		formik.values.otp,
		formik.isSubmitting,
		formik.errors.otp,
		lastFailedOtp,
	]);

	const handleResendCode = async () => {
		if (resendTimerActive) return;
		if (!targetMobile) {
			formik.setFieldError("otp", OTP_COPY.errorMissingMobile);
			return;
		}

		try {
			const response = await getOtp(targetMobile, "mobileNo");
			if (!response?.error) {
				setResendTimerActive(true);
				setResendTimerCount(60);
				formik.setFieldError("otp", undefined);
			} else {
				formik.setFieldError(
					"otp",
					(response?.errorText as string) || OTP_COPY.errorResendFailed,
				);
			}
		} catch (err) {
			formik.setFieldError(
				"otp",
				err instanceof Error ? err.message : OTP_COPY.errorResendUnexpected,
			);
		}
	};

	const handleSkip = () => {
		if (onSkip) {
			// Clear any displayed error / in-flight validation before navigating so
			// the error doesn't flash alongside the skip transition.
			formik.resetForm();
			trackEvent(GTMEvent.MOBILE_VERIFICATION_SKIPPED);
			clientLogger.info(OTP_COPY.logSkip);
			onSkip();
		}
	};

	const otpError = formik.touched.otp ? formik.errors.otp : undefined;
	const submitDisabled =
		formik.isSubmitting ||
		!!otpError ||
		!formik.values.otp ||
		!validateOtp(formik.values.otp) ||
		!formik.values.captcha;
	const showSubmitDisabledStyle = submitDisabled && !formik.isSubmitting;

	return (
		<section className="relative flex flex-col items-stretch w-full h-full bg-[var(--automint-onboarding-bg)] text-[var(--automint-black)] md:bg-[var(--automint-black)] md:text-white rounded-[6px] overflow-hidden">
			<div className="flex flex-col flex-1 justify-center md:justify-start px-5 md:px-7 py-6 md:py-8">
				<div className="mx-auto">
					<Logo
						showText={true}
						invert
						size={40}
						className="[&_span]:!text-[var(--automint-black)] md:[&_span]:!text-white [&_span]:!text-2xl [&_span]:!font-medium"
					/>
				</div>

				<div className="flex flex-col md:flex-1 md:justify-center">
					<div className="mt-14 md:mt-0 2xl:mt-0 text-center">
						<h1 className="text-[26px] md:text-[28px]  leading-tight">
							{OTP_COPY.heroTitle}
						</h1>
						<p className="text-sm text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)] mt-3">
							We&apos;ve sent a verification code to
							<br />
							your mobile{" "}
							{onBack ? (
								<button
									type="button"
									onClick={onBack}
									aria-label="Edit mobile number"
									className="inline-flex items-center gap-1 align-baseline pb-0.5 border-b border-dotted text-[var(--automint-black)] border-[var(--automint-black)] md:text-white md:border-white"
								>
									<span>{targetMobile || "your number"}</span>
									<EditIcon size={14} className="inline-block" />
								</button>
							) : (
								<span className="text-[var(--automint-black)] md:text-white underline decoration-dotted">
									{targetMobile || "your number"}
								</span>
							)}
						</p>
					</div>

					<div className="mt-6">
						<p className="text-sm text-[var(--automint-black)] md:text-white mb-2">
							{OTP_COPY.label}
						</p>
						<OTPInput
							length={5}
							required
							value={formik.values.otp}
							onChange={(otpValue) => {
								formik.setFieldValue("otp", otpValue, true);
							}}
							onComplete={(completedOtp) => {
								if (
									validateOtp(completedOtp) &&
									!formik.isSubmitting &&
									formik.values.captcha
								) {
									formik.setFieldTouched("otp", true, false);
									queueMicrotask(() => {
										formik.submitForm();
									});
								}
							}}
							errorText={otpError}
							autoFocus
							className="[&>div]:!w-full [&>div]:!flex [&>div]:!gap-2 md:[&>div]:!gap-3 [&_input]:!flex-1 [&_input]:!min-w-0 [&_input]:!w-0 [&_input]:!h-12 md:[&_input]:!h-14 [&_input]:!bg-[var(--colors-automint-neutral-200)] [&_input]:!border-[var(--colors-automint-neutral-200)] [&_input]:!text-[var(--automint-black)] md:[&_input]:!bg-[var(--colors-automint-neutral-800)] md:[&_input]:!border-[var(--colors-automint-neutral-800)] md:[&_input]:!text-white [&_input]:!rounded-[6px] [&_input]:!text-lg md:[&_input]:!text-xl"
						/>
					</div>

					{/* Turnstile always rendered (interaction-only = invisible until a
					challenge is actually needed) so it doesn't grow the card mid-typing. */}
					<div className="mt-1">
						<Turnstile
							ref={turnstileRef}
							siteKey={SITE_KEY}
							onSuccess={(token) => formik.setFieldValue("captcha", token)}
							onError={() => formik.setFieldValue("captcha", null)}
							onExpire={() => formik.setFieldValue("captcha", null)}
							options={{
								theme: "light",
								appearance: "interaction-only",
							}}
						/>
					</div>

					<div className="mt-3 flex items-center justify-between text-sm">
						<p className="text-[var(--colors-automint-neutral-500)] md:text-[var(--automint-onboarding-subtitle-dark)]">
							{OTP_COPY.resendPrompt}
						</p>
						<button
							id="onboarding_mobile_otp_resend"
							type="button"
							onClick={handleResendCode}
							disabled={
								formik.isSubmitting || resendTimerActive || !targetMobile
							}
							className="text-[var(--automint-black)] md:text-white underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
						>
							{resendTimerActive
								? OTP_COPY.resendCountdown(resendTimerCount)
								: OTP_COPY.resendCta}
						</button>
					</div>

					<div className="mt-5">
						<button
							id="onboarding_mobile_otp_verify"
							type="button"
							onClick={() => formik.handleSubmit()}
							disabled={submitDisabled}
							className={`w-full h-12 rounded-[6px] text-center font-medium text-sm leading-normal transition-colors disabled:cursor-not-allowed bg-[var(--automint-black)] text-[var(--colors-automint-neon-500)] md:bg-[var(--colors-automint-neon-500)] md:text-[var(--automint-black)] ${showSubmitDisabledStyle ? "opacity-15 md:opacity-100 md:!bg-[var(--automint-onboarding-disabled-bg)] md:!text-[var(--automint-onboarding-disabled-text)]" : ""}`}
						>
							{formik.isSubmitting ? OTP_COPY.verifying : OTP_COPY.verify}
						</button>
					</div>

					{onSkip && (
						<div className="mt-4 mb-2 text-center">
							<button
								id="onboarding_mobile_otp_skip"
								type="button"
								onClick={handleSkip}
								className="text-[var(--automint-black)] md:text-white text-sm underline underline-offset-4 hover:opacity-80"
							>
								{OTP_COPY.skip}
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	);
};

export default StepMobileOtp;
